import { lazy, Suspense } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate, useParams } from "react-router-dom";
import { HelmetProvider } from "react-helmet-async";
import Layout from "./components/Layout";
import Home from "./pages/Home";
import Concierge from "./pages/Concierge";
import Wineries from "./pages/Wineries";
import WineryDetail from "./pages/WineryDetail";
import Blog from "./pages/Blog";
import BlogPost from "./pages/BlogPost";
import AudioTours from "./pages/AudioTours";
import AudioTourDetail from "./pages/AudioTourDetail";
import NotFound from "./pages/NotFound";
import ProtectedRoute from "./components/ProtectedRoute";
import { AudioPlayerProvider } from "./contexts/AudioPlayerContext";
import MiniPlayer from "./components/MiniPlayer";
import FullScreenPlayer from "./components/FullScreenPlayer";
import InstallPrompt from "./components/InstallPrompt";
import { Toaster } from "@/components/ui/sonner";
import ScrollToTop from "./components/ScrollToTop";
import ErrorBoundary from "./components/ErrorBoundary";
import RouteFallback from "./components/RouteFallback";
import RegionalItineraryPage from "./pages/RegionalItineraryPage";
import RegionalGuidesIndex from "./pages/RegionalGuidesIndex";
import TopicalGuidePage from "./pages/TopicalGuidePage";

// Code splitting: the first-paint core (Home, Concierge, the main browse
// pages, Layout and the audio players) stays in the main bundle; admin, checkout
// and other rarely visited pages load on demand. Lazy pages under <Layout> or
// <AdminLayout> suspend inside those layouts; the outer <Suspense> covers the rest.
// Public, rarely visited
const Deals = lazy(() => import("./pages/Deals"));
const NapaValleyGuide = lazy(() => import("./pages/NapaValleyGuide"));
const About = lazy(() => import("./pages/About"));
const Contact = lazy(() => import("./pages/Contact"));
const AIDisclosure = lazy(() => import("./pages/AIDisclosure"));
const MyAudioTours = lazy(() => import("./pages/MyAudioTours"));
const Checkout = lazy(() => import("./pages/Checkout"));
const TourAccess = lazy(() => import("./pages/TourAccess"));
const TripPage = lazy(() => import("./pages/TripPage"));
const SignIn = lazy(() => import("./pages/SignIn"));
const PrivacyPolicy = lazy(() => import("./pages/PrivacyPolicy"));
const TermsOfService = lazy(() => import("./pages/TermsOfService"));
// Admin (heavy: TipTap, recharts, dnd-kit)
const AdminLogin = lazy(() => import("./pages/admin/AdminLogin"));
const AdminForgotPassword = lazy(() => import("./pages/admin/AdminForgotPassword"));
const AdminResetPassword = lazy(() => import("./pages/admin/AdminResetPassword"));
const AdminLayout = lazy(() => import("./components/admin/AdminLayout"));
const AdminDashboardNew = lazy(() => import("./pages/admin/AdminDashboardNew"));
const AdminBlogList = lazy(() => import("./pages/admin/AdminBlogList"));
const AdminBlogEditor = lazy(() => import("./pages/admin/AdminBlogEditor"));
const AdminGuidesList = lazy(() => import("./pages/admin/AdminGuidesList"));
const AdminGuideEditor = lazy(() => import("./pages/admin/AdminGuideEditor"));
const AdminItineraryGuidesList = lazy(() => import("./pages/admin/AdminItineraryGuidesList"));
const AdminItineraryGuideEditor = lazy(() => import("./pages/admin/AdminItineraryGuideEditor"));
const AdminMessages = lazy(() => import("./pages/admin/AdminMessages"));
const AdminWineriesNew = lazy(() => import("./pages/admin/AdminWineriesNew"));
const AdminDealsNew = lazy(() => import("./pages/admin/AdminDealsNew"));
const AdminSettings = lazy(() => import("./pages/admin/AdminSettings"));
const AdminAccount = lazy(() => import("./pages/admin/AdminAccount"));
const AdminSecurity = lazy(() => import("./pages/admin/AdminSecurity"));
const AdminHomePage = lazy(() => import("./pages/admin/AdminHomePage"));
const AdminAudioTours = lazy(() => import("./pages/admin/AdminAudioTours"));
const AdminAiAnalytics = lazy(() => import("./pages/admin/AdminAiAnalytics"));
// Design mockups
const DesignPreview = lazy(() => import("./pages/DesignPreview"));
const DesignPreview2 = lazy(() => import("./pages/DesignPreview2"));
const DesignPreviewLite = lazy(() => import("./pages/DesignPreviewLite"));

// Legacy blog slugs migrated into topical guides (Feb 2026).
// Client-side 301-equivalent redirects preserve inbound backlinks.
const LEGACY_BLOG_REDIRECTS = {
  "top-five-wineries-in-the-napa-valley": "/guides/napa/top-five-wineries",
  "castello-di-amorosa-magnificent-medieval-castle-in-napa-valley": "/guides/napa/castello-di-amorosa",
  "how-is-uber-in-napa-valley-2016-first-hand-experience": "/guides/napa/uber-transportation",
  "napa-valley-first-time-visitor-ultimate-1-day-itinerary": "/guides/napa/first-time-one-day-itinerary",
  "napa-valley-weather-live-updated-daily": "/guides/napa/what-to-wear-by-month",
  "ultimate-guide-to-napa-valley-valentines-day": "/guides/napa/valentines-day",
};

// Small wrapper that redirects legacy blog URLs and otherwise renders the
// normal BlogPost page. Keeps App.js declarative without a HOC file.
const BlogPostWithRedirect = () => {
  const { slug } = useParams();
  const target = LEGACY_BLOG_REDIRECTS[slug];
  if (target) return <Navigate to={target} replace />;
  return <BlogPost />;
};

function App() {
  return (
    <HelmetProvider>
      <AudioPlayerProvider>
        <div className="App">
          <BrowserRouter>
            <ScrollToTop />
            {/* Global players / toaster stay outside the boundary so audio keeps
                playing (and its controls stay usable) if a page crashes. */}
            <ErrorBoundary>
              <Suspense fallback={<RouteFallback />}>
                <Routes>
                  {/* Isolated design mockup — outside <Layout>, no global chrome */}
                  <Route path="/design-preview" element={<DesignPreview />} />
                  <Route path="/design-preview-2" element={<DesignPreview2 />} />
                  <Route path="/design-preview-lite" element={<DesignPreviewLite />} />
                  {/* Public Routes */}
                  <Route path="/" element={<Layout />}>
                    <Route index element={<Home />} />
                    <Route path="concierge" element={<Concierge />} />
                    <Route path="wineries" element={<Wineries />} />
                    <Route path="wine-country-guides" element={<RegionalGuidesIndex />} />
                    <Route path="wineries/:region/:intentPath" element={<RegionalItineraryPage />} />
                    <Route path="guides/:region/:slug" element={<TopicalGuidePage />} />
                    <Route path="wineries/:id" element={<WineryDetail />} />
                    <Route path="blog" element={<Blog />} />
                    <Route path="blog/:slug" element={<BlogPostWithRedirect />} />
                    <Route path="deals" element={<Deals />} />
                    <Route path="audio-tours" element={<AudioTours />} />
                    <Route path="audio-tours/access/:token" element={<TourAccess />} />
                    <Route path="audio-tours/:tourId" element={<AudioTourDetail />} />
                    <Route path="my-audio-tours" element={<MyAudioTours />} />
                    <Route path="checkout/:tourId" element={<Checkout />} />
                    <Route path="trip/:slug" element={<TripPage />} />
                    <Route path="signin" element={<SignIn />} />
                    {/* Canonical sign-in URL is /signin — redirect legacy aliases */}
                    <Route path="sign-in" element={<Navigate to="/signin" replace />} />
                    <Route path="login" element={<Navigate to="/signin" replace />} />
                    <Route path="napa-valley" element={<NapaValleyGuide />} />
                    <Route path="about" element={<About />} />
                    <Route path="contact" element={<Contact />} />
                    <Route path="ai-disclosure" element={<AIDisclosure />} />
                    <Route path="privacy" element={<PrivacyPolicy />} />
                    <Route path="terms" element={<TermsOfService />} />
                    {/* Catch-all — renders a noindex 404 page for any unmatched path.
                        This is the primary fix for "Duplicate without user-selected
                        canonical" in GSC: old WordPress flat-slug URLs
                        (e.g. /cali/, /foodgasm/, /best-napa-valley-wine/) now get a
                        noindex meta tag so Google drops them from its index. */}
                    <Route path="*" element={<NotFound />} />
                  </Route>
              
                  {/* Admin Routes */}
                  <Route path="/admin" element={<AdminLogin />} />
                  <Route path="/admin/login" element={<Navigate to="/admin" replace />} />
                  <Route path="/admin/forgot-password" element={<AdminForgotPassword />} />
                  <Route path="/admin/reset-password" element={<AdminResetPassword />} />
                  <Route path="/admin/dashboard" element={<ProtectedRoute><AdminLayout><AdminDashboardNew /></AdminLayout></ProtectedRoute>} />
                  <Route path="/admin/blogs" element={<ProtectedRoute><AdminLayout><AdminBlogList /></AdminLayout></ProtectedRoute>} />
                  <Route path="/admin/blogs/new" element={<ProtectedRoute><AdminLayout><AdminBlogEditor /></AdminLayout></ProtectedRoute>} />
                  <Route path="/admin/blogs/edit/:id" element={<ProtectedRoute><AdminLayout><AdminBlogEditor /></AdminLayout></ProtectedRoute>} />
                  <Route path="/admin/guides" element={<ProtectedRoute><AdminLayout><AdminGuidesList /></AdminLayout></ProtectedRoute>} />
                  <Route path="/admin/guides/new" element={<ProtectedRoute><AdminLayout><AdminGuideEditor /></AdminLayout></ProtectedRoute>} />
                  <Route path="/admin/guides/edit/:id" element={<ProtectedRoute><AdminLayout><AdminGuideEditor /></AdminLayout></ProtectedRoute>} />
                  <Route path="/admin/itinerary-guides" element={<ProtectedRoute><AdminLayout><AdminItineraryGuidesList /></AdminLayout></ProtectedRoute>} />
                  <Route path="/admin/itinerary-guides/new" element={<ProtectedRoute><AdminLayout><AdminItineraryGuideEditor /></AdminLayout></ProtectedRoute>} />
                  <Route path="/admin/itinerary-guides/edit/:id" element={<ProtectedRoute><AdminLayout><AdminItineraryGuideEditor /></AdminLayout></ProtectedRoute>} />
                  <Route path="/admin/wineries" element={<ProtectedRoute><AdminLayout><AdminWineriesNew /></AdminLayout></ProtectedRoute>} />
                  <Route path="/admin/wineries/new" element={<ProtectedRoute><AdminLayout><AdminWineriesNew /></AdminLayout></ProtectedRoute>} />
                  <Route path="/admin/deals" element={<ProtectedRoute><AdminLayout><AdminDealsNew /></AdminLayout></ProtectedRoute>} />
                  <Route path="/admin/deals/new" element={<ProtectedRoute><AdminLayout><AdminDealsNew /></AdminLayout></ProtectedRoute>} />
                  <Route path="/admin/audio-tours" element={<ProtectedRoute><AdminLayout><AdminAudioTours /></AdminLayout></ProtectedRoute>} />
                  <Route path="/admin/messages" element={<ProtectedRoute><AdminLayout><AdminMessages /></AdminLayout></ProtectedRoute>} />
                  <Route path="/admin/analytics" element={<Navigate to="/admin/analytics/ai" replace />} />
                  <Route path="/admin/analytics/ai" element={<ProtectedRoute><AdminLayout><AdminAiAnalytics /></AdminLayout></ProtectedRoute>} />
                  <Route path="/admin/security" element={<ProtectedRoute><AdminLayout><AdminSecurity /></AdminLayout></ProtectedRoute>} />
                  <Route path="/admin/homepage" element={<ProtectedRoute><AdminLayout><AdminHomePage /></AdminLayout></ProtectedRoute>} />
                  <Route path="/admin/settings" element={<ProtectedRoute><AdminLayout><AdminSettings /></AdminLayout></ProtectedRoute>} />
                  <Route path="/admin/account" element={<ProtectedRoute><AdminLayout><AdminAccount /></AdminLayout></ProtectedRoute>} />
                </Routes>
              </Suspense>
            </ErrorBoundary>
          </BrowserRouter>
          
          {/* Global Audio Players */}
          <MiniPlayer />
          <FullScreenPlayer />
          
          {/* PWA Install Prompt */}
          <InstallPrompt />
          
          <Toaster position="top-right" />
        </div>
      </AudioPlayerProvider>
    </HelmetProvider>
  );
}

export default App;
